/**
 * `brush.stroke` / `brush.stroke.append` events for the swept brush (large-volume plan §2.3, S9).
 *
 * - `brush.stroke` is an undo boundary; `brush.stroke.append` is a draft-only increment.
 * - The reducer folds the payload with `applyBrushStroke` (src/brush/rasterize.ts). The stroke's
 *   sample centres are resolved by the controller (placement rule applied there), so the reducer never
 *   picks or places.
 * - `combineBrushEvents` merges appends into one stroke only when the brush snapshots are deep-equal
 *   and the chain is continuous (`prev` of the next event is the last sample of the previous one), so
 *   the fold over the merged event equals the fold over the parts. Legacy scribble coalescing is
 *   untouched (see plan §6).
 * - Tapered strokes (nib, `ScribbleSettings.nib`) carry per-sample `radii` and the joint radius
 *   `prevR`; they merge only when the joint radius matches too.
 */

import type { EventTuple } from './types'
import type { HandlerCtx } from './eventRegistry'
import type { VoxelData } from '@/voxelTypes'
import type { Position } from '@/voxelMath'
import {
  applyBrushStroke,
  brushStrokeCellBounds,
  brushVerbIsScoped,
  brushVerbNeedsValue,
  keySetBrushTarget,
  normalizeKeepTypes,
  sampleCount,
  sampleRadiusFx,
  voxelMapBrushTarget,
  type BrushSnapshot,
  type BrushStrokePayload,
  type BrushVerb,
} from '@/brush/rasterize'

export type { BrushSnapshot, BrushStrokePayload, BrushVerb }

export const BRUSH_STROKE = 'brush.stroke' as const
export const BRUSH_STROKE_APPEND = 'brush.stroke.append' as const
export type BrushStrokeEventType = typeof BRUSH_STROKE | typeof BRUSH_STROKE_APPEND
export type BrushStrokeEvent = readonly [BrushStrokeEventType, BrushStrokePayload]

export function isBrushStrokeEventType(type: unknown): type is BrushStrokeEventType {
  return type === BRUSH_STROKE || type === BRUSH_STROKE_APPEND
}

export function isBrushStrokeEvent(event: EventTuple): event is BrushStrokeEvent {
  return isBrushStrokeEventType(event[0])
}

// ----------------------------------------------------------------------------
// Handlers
// ----------------------------------------------------------------------------

type Handler = (ctx: HandlerCtx, event: EventTuple) => void

const brushStrokeHandler: Handler = (ctx, event) => {
  const [, payload] = event as BrushStrokeEvent
  const meta = ctx.meta as any
  applyBrushStroke(voxelMapBrushTarget(ctx.base), payload, {
    bounds: meta.bounds,
    mirrors: meta.mirrors || null,
    // Coat reads the document as it was before the stroke: the draft base while the stroke is being
    // drawn (its appends must not coat onto the stroke's own paint), else the base before this event.
    ...(payload.brush.verb === 'coat' && ctx.strokeBase ? { reference: ctx.strokeBase } : {}),
  })
}

export const brushStrokeHandlers: Record<string, Handler> = {
  [BRUSH_STROKE]: brushStrokeHandler,
  [BRUSH_STROKE_APPEND]: brushStrokeHandler,
}

// ----------------------------------------------------------------------------
// Coalescing
// ----------------------------------------------------------------------------

export function sameBrushSnapshot(a: BrushSnapshot, b: BrushSnapshot): boolean {
  if (a === b) return true
  if (a.v !== b.v || a.verb !== b.verb || a.rFx !== b.rFx) return false
  // Lumpy strokes with a different lump, lattice or seed never merge: their per-cell shells differ.
  if ((a.lumpFx ?? 0) !== (b.lumpFx ?? 0) || a.lumpCell !== b.lumpCell || a.seed !== b.seed) return false
  // Strokes with different scopes or protected types never merge: their per-cell rules differ.
  if (!sameKeep(a.keep, b.keep)) return false
  // Coat strokes with a different thickness or recolouring rule never merge.
  if ((a.coatFx ?? 0) !== (b.coatFx ?? 0) || !!a.coatRepaint !== !!b.coatRepaint) return false
  return sameVoxelValue(a.value, b.value) && sameVoxelValue(a.onlyValue, b.onlyValue)
}

function sameKeep(a: readonly string[] | undefined, b: readonly string[] | undefined): boolean {
  const na = a?.length ?? 0
  const nb = b?.length ?? 0
  if (na !== nb) return false
  for (let i = 0; i < na; i++) if (a![i] !== b![i]) return false
  return true
}

function sameVoxelValue(a: VoxelData | undefined, b: VoxelData | undefined): boolean {
  if (a === b) return true
  if (!a || !b) return false
  return a.type === b.type
    && a.color === b.color
    && a.material === b.material
    && a.face === b.face
    && a.topFace === b.topFace
}

/**
 * Merge two brush events into one `brush.stroke`, or return null when they cannot be merged exactly:
 * - either event is not a brush event, or both are already `brush.stroke` (separate strokes);
 * - the snapshots differ (value, verb or radius changed);
 * - `next.prev` is set but is not `prev`'s last sample (the fold would differ).
 * A `next` without `prev` starts a fresh sphere, recorded as a break in the merged stroke.
 */
export function combineBrushEvents(prev: EventTuple, next: EventTuple): EventTuple | null {
  if (!isBrushStrokeEvent(prev) || !isBrushStrokeEvent(next)) return null
  if (prev[0] === BRUSH_STROKE && next[0] === BRUSH_STROKE) return null
  const a = prev[1]
  const b = next[1]
  if (!sameBrushSnapshot(a.brush, b.brush)) return null
  const na = sampleCount(a)
  const nb = sampleCount(b)
  if (na === 0) return makeStroke(b)
  if (nb === 0) return makeStroke(a)

  const lastA = a.samples.slice((na - 1) * 3, na * 3)
  const tapered = !!(a.radii || b.radii)
  const breaks: number[] = a.breaks ? [...a.breaks] : []
  if (b.prev && b.prev.length >= 3) {
    if (b.prev[0] !== lastA[0] || b.prev[1] !== lastA[1] || b.prev[2] !== lastA[2]) return null
    // A tapered chain also needs the radius at the joint to match, or the fold would differ.
    if (tapered && prevRadiusFx(b) !== sampleRadiusFx(a, na - 1)) return null
  } else {
    breaks.push(na)
  }
  if (b.breaks) for (const i of b.breaks) breaks.push(i + na)

  const merged: BrushStrokePayload = {
    brush: a.brush,
    samples: [...a.samples, ...b.samples],
    ...(a.prev && a.prev.length >= 3 ? { prev: a.prev } : {}),
    ...(breaks.length ? { breaks: dedupeSorted(breaks) } : {}),
    ...(tapered ? { radii: [...radiiOf(a, na), ...radiiOf(b, nb)] } : {}),
    ...(tapered && a.prev && a.prev.length >= 3 ? { prevR: prevRadiusFx(a) } : {}),
  }
  if (DEV) assertFoldSplitInvariant(a, b, merged)
  return [BRUSH_STROKE, merged] as unknown as EventTuple
}

function radiiOf(p: BrushStrokePayload, n: number): number[] {
  const out: number[] = new Array(n)
  for (let i = 0; i < n; i++) out[i] = sampleRadiusFx(p, i)
  return out
}

function prevRadiusFx(p: BrushStrokePayload): number {
  const r = p.prevR
  if (r == null || !Number.isFinite(r)) return p.brush.rFx
  return Math.max(0, Math.min(p.brush.rFx, Math.round(r)))
}

function makeStroke(p: BrushStrokePayload): EventTuple {
  return [BRUSH_STROKE, p] as unknown as EventTuple
}

function dedupeSorted(xs: number[]): number[] {
  const sorted = [...xs].sort((p, q) => p - q)
  const out: number[] = []
  for (const x of sorted) if (out.length === 0 || out[out.length - 1] !== x) out.push(x)
  return out
}

// Dev-only: fold(a) ∪ fold(b) must equal fold(merged). Bounded so long strokes do not make commits slow.
// The assertion compares visited cell sets: every verb (scoped ones included) is an idempotent rule
// applied per visited cell, so equal cell sets under one snapshot give equal results. The fold runs
// with the payload's own snapshot, so lumpy strokes (lumpFx > 0) are asserted with their noise.
const DEV: boolean = (() => {
  try { return !!(import.meta as any).env?.DEV } catch { return false }
})()
const SPLIT_ASSERT_MAX_SAMPLES = 48
// Cells visited grow with r^3: a sunk "lens" bead (a sphere of radius ~ w^2 / 2h, /paint-bench) made
// dev commits take seconds at 512 wide. Big brushes skip the assertion.
const SPLIT_ASSERT_MAX_RADIUS_FX = 16 * 16
const SPLIT_ASSERT_BOUNDS = { x: 512, y: 512, z: 512 }

export function assertFoldSplitInvariant(a: BrushStrokePayload, b: BrushStrokePayload, merged: BrushStrokePayload): void {
  if (sampleCount(merged) > SPLIT_ASSERT_MAX_SAMPLES) return
  if (merged.brush.rFx > SPLIT_ASSERT_MAX_RADIUS_FX) return
  const split = keySetBrushTarget()
  applyBrushStroke(split, a, { bounds: SPLIT_ASSERT_BOUNDS })
  applyBrushStroke(split, b, { bounds: SPLIT_ASSERT_BOUNDS })
  const whole = keySetBrushTarget()
  applyBrushStroke(whole, merged, { bounds: SPLIT_ASSERT_BOUNDS })
  if (split.keys.size !== whole.keys.size) {
    throw new Error(`brush.stroke split invariant violated: ${split.keys.size} cells split vs ${whole.keys.size} merged`)
  }
  for (const k of split.keys) {
    if (!whole.keys.has(k)) throw new Error('brush.stroke split invariant violated: cell set differs')
  }
}

// ----------------------------------------------------------------------------
// Bounds inference and normalization
// ----------------------------------------------------------------------------

/** Cell-space bounds a stroke can touch, or null when it has no samples. */
export function brushStrokeBounds(payload: BrushStrokePayload): { min: Position; max: Position } | null {
  return brushStrokeCellBounds(payload)
}

const VERBS = new Set<BrushVerb>(['place', 'subtract', 'fill', 'subtractIf', 'repaintIf'])

/** Normalize a JSON-shaped payload (used by the document builder). Throws on malformed input. */
export function normalizeBrushStrokePayload(
  raw: unknown,
  normalizeVoxel: (voxel: unknown) => VoxelData,
): BrushStrokePayload {
  if (!raw || typeof raw !== 'object') throw new Error('brush.stroke payload must be an object')
  const p = raw as Record<string, unknown>
  const brushRaw = p.brush
  if (!brushRaw || typeof brushRaw !== 'object') throw new Error('brush.stroke payload must include a brush snapshot')
  const br = brushRaw as Record<string, unknown>
  const verb = br.verb
  if (!VERBS.has(verb as BrushVerb)) throw new Error(`brush.stroke: invalid verb '${String(verb)}'`)
  const rFx = Number(br.rFx)
  if (!Number.isFinite(rFx) || rFx < 0) throw new Error(`brush.stroke: invalid rFx '${String(br.rFx)}'`)
  const brush: BrushSnapshot = { v: 1, verb: verb as BrushVerb, rFx: Math.round(rFx) }
  if (br.value != null) brush.value = normalizeVoxel(br.value)
  else if (brushVerbNeedsValue(brush.verb)) throw new Error(`brush.stroke: ${brush.verb} strokes need a value`)
  if (br.onlyValue != null) brush.onlyValue = normalizeVoxel(br.onlyValue)
  else if (brushVerbIsScoped(brush.verb)) throw new Error(`brush.stroke: ${brush.verb} strokes need an onlyValue scope`)
  // Lump fields (B4): optional; logs from before B4 have none and replay as plain sweeps.
  if (br.lumpFx != null) {
    const lumpFx = Number(br.lumpFx)
    if (!Number.isFinite(lumpFx) || lumpFx < 0) throw new Error(`brush.stroke: invalid lumpFx '${String(br.lumpFx)}'`)
    brush.lumpFx = Math.round(lumpFx)
  }
  if (br.lumpCell != null) {
    const lumpCell = Number(br.lumpCell)
    if (!Number.isInteger(lumpCell) || lumpCell < 1) throw new Error(`brush.stroke: invalid lumpCell '${String(br.lumpCell)}'`)
    brush.lumpCell = lumpCell
  }
  if (br.seed != null) {
    const seed = Number(br.seed)
    if (!Number.isInteger(seed)) throw new Error(`brush.stroke: invalid seed '${String(br.seed)}'`)
    brush.seed = seed | 0
  }
  if (brush.lumpFx && brush.seed == null) throw new Error('brush.stroke: lumpy strokes need a seed')
  // Protected types: optional; logs from before it existed have none and erase everything covered.
  if (br.keep != null) {
    if (!Array.isArray(br.keep)) throw new Error('brush.stroke: keep must be an array of types')
    const keep = normalizeKeepTypes(br.keep.map(String))
    if (keep) brush.keep = keep
  }

  const samples = normalizeFxArray(p.samples, 'samples')
  if (samples.length % 3 !== 0) throw new Error('brush.stroke: samples must hold xyz triples')
  const out: BrushStrokePayload = { brush, samples }
  if (p.prev != null) {
    const prev = normalizeFxArray(p.prev, 'prev')
    if (prev.length !== 3) throw new Error('brush.stroke: prev must be one xyz triple')
    out.prev = prev
  }
  if (p.radii != null) {
    const radii = normalizeFxArray(p.radii, 'radii')
    if (radii.length * 3 !== samples.length) throw new Error('brush.stroke: radii must hold one radius per sample')
    if (radii.some((r) => r < 0)) throw new Error('brush.stroke: radii must not be negative')
    out.radii = radii
  }
  if (p.prevR != null) {
    const prevR = Math.round(Number(p.prevR))
    if (!Number.isFinite(prevR) || prevR < 0) throw new Error(`brush.stroke: invalid prevR '${String(p.prevR)}'`)
    out.prevR = prevR
  }
  if (p.breaks != null) {
    if (!Array.isArray(p.breaks)) throw new Error('brush.stroke: breaks must be an array')
    out.breaks = p.breaks.map((b) => {
      const i = Number(b)
      if (!Number.isInteger(i) || i < 0) throw new Error(`brush.stroke: invalid break index '${String(b)}'`)
      return i
    })
  }
  return out
}

function normalizeFxArray(value: unknown, field: string): number[] {
  if (!Array.isArray(value)) throw new Error(`brush.stroke: ${field} must be an array`)
  return value.map((v) => {
    const n = Number(v)
    if (!Number.isFinite(n)) throw new Error(`brush.stroke: ${field} holds a non-finite coordinate`)
    return Math.round(n)
  })
}
